import React, { Suspense, lazy, useEffect } from 'react';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
// pages
import { withProfiler } from '@sentry/react';
import TagManager from 'react-gtm-module';
import { useSelector } from 'react-redux';
import { selectVendorGTMCode } from 'store/vendorSlice/selectors';
import LoadingSpinner from './components/Common/LoadingSpinner';
import ErrorFallback from './components/ErrorFallback/ErrorFallback';
import Layout from './components/Layout/Layout';
import { NotFoundPage } from './components/NotFoundPage';
import AddonsPage from './pages/AddonsPage';
import CalendarPage from './pages/CalendarPage/CalendarPage';
import { ContactInfoCartPage } from './pages/ContactInfoCartPage';
import EventDetailsPage from './pages/EventDetailsPage/index';
import FreeEventRegistrationPage from './pages/FreeEventRegistrationPage/FreeEventRegistrationPage';
import HomePage from './pages/HomePage/index';
import OrderConfirmationPage from './pages/OrderConfirmationPage';
import RegistrationFormsPage from './pages/RegistrationFormsPage';
import ReserveSeatsPage from './pages/ReserveSeatsPage/ReserveSeatsPage';
import TicketSalePage from './pages/TicketSalePage';
import CartCancelRedirect from './redirects/CartCancelRedirect';
import RedirectToBilling from './redirects/RedirectToBilling/RedirectToBilling';
import RedirectToPaymentPage from './redirects/RedirectToPaymentPage';

const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const OrderCancelRedirect = lazy(
  () => import('./redirects/OrderCancelRedirect'),
);

const RegistrationFormPage = lazy(() => import('./pages/RegistrationFormPage'));

const router = createBrowserRouter(
  [
    {
      element: <Layout />,
      errorElement: <ErrorFallback />,
      children: [
        {
          path: '/org/:vendorId',
          element: <HomePage />,
        },
        {
          path: '/event/:eventSlug',
          element: <EventDetailsPage />,
        },
        {
          path: '/event/:eventSlug/buy-tickets',
          element: <TicketSalePage />,
        },
        {
          path: '/event/:eventSlug/registration',
          element: <FreeEventRegistrationPage />,
        },
        {
          path: '/event/:eventSlug/reserve-seats',
          element: <ReserveSeatsPage />,
        },
        {
          path: '/event/:eventSlug/calendar',
          element: <CalendarPage />,
        },
        {
          path: '/event/:eventSlug/buy-tickets/addons',
          element: <AddonsPage />,
        },
        {
          path: '/event/:eventSlug/buy-tickets/billing-information',
          element: <ContactInfoCartPage />,
        },
        {
          path: '/order/confirmation',
          element: <OrderConfirmationPage />,
        },
        {
          path: '/order/redirect-to-payment-page',
          element: <RedirectToPaymentPage />,
        },
        {
          path: '/order/cancel',
          element: (
            <Suspense fallback={<LoadingSpinner />}>
              <OrderCancelRedirect />,
            </Suspense>
          ),
        },
        {
          path: '/cart/cancel',
          element: <CartCancelRedirect />,
        },
        {
          path: '/order/assign-tickets',
          element: <RegistrationFormsPage />,
        },
        {
          path: '/order/complete-attendee-form',
          element: (
            <Suspense fallback={<LoadingSpinner />}>
              <RegistrationFormPage />
            </Suspense>
          ),
        },
        {
          path: '/reset-password/:resetPasswordToken/:hashedEmail',
          element: (
            <Suspense fallback={<LoadingSpinner />}>
              <ResetPasswordPage />
            </Suspense>
          ),
        },
        {
          path: '/event/redirect-to-billing',
          element: <RedirectToBilling />,
        },
        {
          path: '/404',
          element: <NotFoundPage />,
        },
        {
          path: '/*',
          element: <NotFoundPage />,
        },
      ],
    },
  ],
  {
    basename: process.env.REACT_APP_BASENAME || '/',
  },
);

const App = () => {
  const gtmId = useSelector(selectVendorGTMCode);

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') {
      TagManager.initialize({
        gtmId: process.env.REACT_APP_OVEIT_GTM_ID as string,
      });
    }
  }, []);

  useEffect(() => {
    if (gtmId) {
      TagManager.initialize({
        gtmId,
      });
    }
  }, [gtmId]);

  return <RouterProvider router={router} />;
};

export default withProfiler(App);
